// Создаём отдельный компонент, который автоматически строит хлебные крошки по pathname.
'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react'; // ✅ Импортируем useMemo

const fallbackSiteUrl = (process.env.NEXT_PUBLIC_SITE_URL || 'https://tomsktoday.ru').replace(
  /\/$/,
  '',
);

function toAbsoluteUrl(path: string) {
  if (!path.startsWith('/')) return path;

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }

  return `${fallbackSiteUrl}${path}`;
}

// ✅ Улучшение: можно вынести за пределы компонента, это константа
const breadcrumbNameMap: Record<string, string> = {
  finance: 'Финансы',
  'cb-rates': 'Курс валют ЦБ РФ',
  'currency-chart': 'График валют',
  converter: 'Конвертер валют',
  crypto: 'Курсы криптовалют',
  news: 'Новости',
  // Дополняй по мере надобности
};

export default function Breadcrumbs() {
  const pathname = usePathname();

  // ✅ Оптимизация: Оборачиваем все вычисления в useMemo.
  // Они будут выполняться только при смене pathname.
  const { breadcrumbs, structuredData } = useMemo(() => {
    if (!pathname) {
      return { breadcrumbs: [], structuredData: null };
    }

    const pathParts = pathname.split('/').filter(Boolean);
    const crumbs = pathParts.map((part, idx) => {
      const href = '/' + pathParts.slice(0, idx + 1).join('/');
      const name = breadcrumbNameMap[part] || decodeURIComponent(part);
      return { name, href };
    });

    // ✅ SEO: Генерируем JSON-LD для поисковиков
    const ldJson = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Главная',
          item: toAbsoluteUrl('/'),
        },
        ...crumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          position: idx + 2, // Начинаем со 2-й позиции
          name: crumb.name,
          item: toAbsoluteUrl(crumb.href),
        })),
      ],
    };

    return {
      breadcrumbs: crumbs,
      structuredData: JSON.stringify(ldJson),
    };
  }, [pathname]);

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    // ✅ A11y: Добавляем aria-label для <nav>
    // ✅ Адаптивность: Добавляем flex flex-wrap
    <nav aria-label="breadcrumb" className="text-sm text-gray-600 dark:text-gray-300 my-3">
      {/* ✅ SEO: Внедряем структурированные данные. Поисковики это увидят. */}
      {structuredData && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
      )}

      {/* ✅ A11y: Используем <ol> и <li> для семантически верного списка */}
      <ol className="flex flex-wrap items-center space-x-1">
        <li>
          <Link href="/" className="text-black/70 hover:underline dark:text-blue-400">
            Главная
          </Link>
        </li>
        {breadcrumbs.map((crumb, idx) => (
          <li key={crumb.href} className="flex items-center">
            <span className="mx-1 text-gray-400">/</span>
            {idx === breadcrumbs.length - 1 ? (
              // ✅ A11y: aria-current для текущей страницы
              <span className="text-gray-300 dark:text-gray-400" aria-current="page">
                {crumb.name}
              </span>
            ) : (
              <Link href={crumb.href} className="text-black/70 hover:underline dark:text-blue-400">
                {crumb.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
